/** What an engaged auditor can read - **ADR-0093, gate 16**.
 *
 * An engagement names filings, and the sites an auditor reads follow from them. Since ADR-0093 a
 * site is read by every company that owns it **in its history**, not by the company that created it:
 * hand a site from one company to another and the later owner's auditor starts reading it; correct
 * the owner away and the former owner's auditor stops. An Admin who has recorded a handover has no
 * other way to see that, short of signing in as the auditor, so this is the control that answers
 * *"what will they actually see?"*
 *
 * **It shows the database's answer, not a second implementation of it.** The server reads the sites
 * under the same row-level security the auditor is clamped by, so this cannot disagree with their
 * screens - the property a hand-written list of "sites for company X" would lose the first time the
 * rule moved.
 *
 * **A read that fails says so.** `useEngagementReach` throws rather than defaulting, and `Loaded`
 * tells loading, failed and answered apart. An engagement that reaches **no site** is rendered as a
 * finding (an auditor with nothing to test), not as an empty list that looks like loading.
 *
 * Offered for an auditor engaged for named filings only. An auditor with no filing named reads
 * everything and already has a warning on the row; listing every site beside it would make the
 * absence of a restriction look like one.
 */

import { useState } from "react";

import { useEngagementReach } from "../api/queries";
import type { Member } from "../api/queries";
import { Loaded } from "../components/Loaded";
import own from "./People.module.css";
import styles from "./Screen.module.css";

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function EngagementReach({ member }: { member: Member }) {
  const [open, setOpen] = useState(false);
  const reach = useEngagementReach(member.user_id, open);

  if (member.entity_scope !== "named") return null;

  if (!open) {
    return (
      <span className={cls(own.rowActions)}>
        <button
          type="button"
          className={styles.fixLink}
          data-testid={`show-reach-${member.user_id}`}
          onClick={() => setOpen(true)}
        >
          Show what this auditor can read
        </button>
      </span>
    );
  }

  return (
    <div className={cls(own.can)} data-testid={`reach-${member.user_id}`}>
      <Loaded query={reach} of="what this auditor can read">
        {(data) => (
          <>
            <p data-testid="reach-companies">
              Companies: {data.companies.map((company) => company.name).join(", ") || "none"}
            </p>
            {data.sites.length === 0 ? (
              <p className={cls(own.warn)} data-testid="reach-no-sites">
                No site is in this auditor&rsquo;s reach, so there is nothing for them to test. If a
                site was recently handed to a company they are engaged for, check its ownership
                history.
              </p>
            ) : (
              <ul data-testid="reach-sites">
                {data.sites.map((site) => (
                  <li key={site.facility_id} data-testid={`reach-site-${site.facility_id}`}>
                    {site.name}
                    <span className={cls(own.can)}> - owned by {site.owner_names.join(" and ")}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.truncated ? (
              <p data-testid="reach-truncated">
                Showing {data.sites.length} of {data.site_count} sites.
              </p>
            ) : null}
          </>
        )}
      </Loaded>
      <button
        type="button"
        className={styles.fixLink}
        data-testid={`hide-reach-${member.user_id}`}
        onClick={() => setOpen(false)}
      >
        Hide
      </button>
    </div>
  );
}
